// Kullanıcı detay sayfası
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  FiEdit, 
  FiMail, 
  FiPhone, 
  FiMapPin,
  FiUser,
  FiCalendar,
  FiShield,
  FiKey,
  FiTrash2,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import { CgMenuGridO } from 'react-icons/cg';
import { IoChevronBackOutline } from 'react-icons/io5';
import { userService } from '../../services/userService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import UserModal from '../../components/Users/UserModal';
import UserGoalModal from '../../components/Users/UserGoalModal';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import toast from 'react-hot-toast';

const UserDetail = () => {
  const { user: currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showGoalEditModal, setShowGoalEditModal] = useState(false);
  const [deletingGoal, setDeletingGoal] = useState(null);
  const [showGoalDeleteModal, setShowGoalDeleteModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showGoals, setShowGoals] = useState(false);

  // Kullanıcı detaylarını al
  const { data: userData, isLoading, refetch } = useQuery(
    ['user', id],
    () => userService.getUser(id),
    { enabled: !!id }
  );
  const { data: goalsData } = useQuery(
    ['user_goals', userData?.user?.id],
    () => userService.getUserGoals(userData?.user?.id),
    { enabled: !!userData?.user?.id }
  );

  // Fonksiyonlar
  const handleEdit = () => {
    setShowEditModal(true);
    setShowActionMenu(false);
  };

  const handlePasswordReset = () => {
    setShowPasswordReset(true);
    setShowActionMenu(false);
    setPasswordData({ password: '', confirmPassword: '' });
    setPasswordErrors({});
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
    setShowActionMenu(false);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setShowGoalEditModal(true);
  };

  const handleDeleteGoal = (goal) => {
    setDeletingGoal(goal);
    setShowGoalDeleteModal(true);
  };

  const confirmDeleteGoal = async () => {
    if (!deletingGoal) return;
    
    try {
      await userService.deleteUserGoal(deletingGoal.id);
      toast.success('Hedef silindi');
      refetch();
      window.location.reload();
    } catch (error) {
      toast.error('Hedef silinemedi');
    } finally {
      setShowGoalDeleteModal(false);
      setDeletingGoal(null);
    }
  };

  const toggleActionMenu = () => {
    setShowActionMenu(!showActionMenu);
  };

  const confirmDelete = async () => {
    try {
      await userService.deleteUser(user.id);
      toast.success('Kullanıcı başarıyla silindi');
      navigate('/users');
    } catch (error) {
      toast.error('Kullanıcı silme hatası: ' + error.message);
    }
  };

  // Menü dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu && !event.target.closest('.action-menu')) {
        setShowActionMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionMenu]);

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Hata temizle
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.password) {
      errors.password = 'Şifre gerekli';
    } else if (passwordData.password.length < 6) {
      errors.password = 'Şifre en az 6 karakter olmalı';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Şifre tekrarı gerekli';
    } else if (passwordData.password !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor';
    }
    
    return errors;
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validatePassword();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    try {
      await userService.updatePassword(user.user_uuid, passwordData.password);
      toast.success('Şifre başarıyla güncellendi');
      setShowPasswordReset(false);
      setPasswordData({ password: '', confirmPassword: '' });
    } catch (error) {
      toast.error('Şifre güncelleme hatası: ' + error.message);
    }
  };

  if (isLoading) {
    return <LoadingSpinner text="Kullanıcı bilgileri yükleniyor..." />;
  }

  const user = userData?.user;

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">Kullanıcı bulunamadı</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            title="Geri"
          >
            <IoChevronBackOutline size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {user.user_fullname}
            </h1>
      
          </div>
        </div>
        
        <div className="relative action-menu">
          <button 
            onClick={toggleActionMenu}
            className="btn-primary flex items-center"
          >
            <CgMenuGridO className="mr-2" size={16} />
            İşlemler
          </button>
          
          {showActionMenu && (
            <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-50 min-w-48">
              <div className="py-1">
                <button
                  onClick={handleEdit}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FiEdit className="mr-3" size={16} />
                  Düzenle
                </button>
                <button
                  onClick={handlePasswordReset}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FiKey className="mr-3" size={16} />
                  Şifre Sıfırla
                </button>
                <button
                  onClick={() => {
                    setShowGoalModal(true);
                    setShowActionMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FiUser className="mr-3" size={16} />
                  + Hedef Ekle
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FiTrash2 className="mr-3" size={16} />
                  Sil
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Detaylı bilgiler */}
        <div className="space-y-6">
          {/* Kişisel bilgiler */}
          <div className="card p-0 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Kişisel Bilgiler
              </h3>
            </div>
            <div className="p-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <FiMail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Email
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_email}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiPhone className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Telefon
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_gsm}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiMapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Departman
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_department}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiShield className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Kullanıcı Tipi
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_type === 'admin' ? 'Yönetici' :
                     user.user_type === 'manager' ? 'Müdür' : 'Temsilci'}
                  </p>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Hesap bilgileri */}
          <div className="card p-0 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Hesap Bilgileri
              </h3>
            </div>
            <div className="p-6">
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Kullanıcı Adı
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_username}
                  </p>
                </div>
                <span className="text-xs text-gray-400">Değiştirilemez</span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Hesap Durumu
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_status ? 'Aktif' : 'Pasif'}
                  </p>
                </div>
                <span className={user.user_status ? 'status-active' : 'status-inactive'}>
                  {user.user_status ? 'Aktif' : 'Pasif'}
                </span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Yönetici
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.manager_name || 'Yönetici atanmamış'}
                  </p>
                </div>
                <span className="text-xs text-gray-400">Opsiyonel</span>
              </div>

              <div className="flex items-center justify-between py-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Son Giriş
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_last_login ? new Date(user.user_last_login).toLocaleString('tr-TR') : 'Hiç giriş yapmamış'}
                  </p>
                </div>
                <FiCalendar className="text-gray-400" size={16} />
              </div>
            </div>
            </div>
          </div>

          {/* Yıllık Hedefler - sadece admin ve manager */}
          {['admin', 'manager'].includes(currentUser?.user_type) && (
            <div className="card p-0 overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Yıllık Hedefler</h3>
                  <button
                    onClick={() => setShowGoals((v) => !v)}
                    className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white p-1 rounded"
                    aria-label={showGoals ? 'Hedefleri gizle' : 'Hedefleri göster'}
                    title={showGoals ? 'Hedefleri gizle' : 'Hedefleri göster'}
                  >
                    {showGoals ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>
              <div className="p-6">
                {/* Mobil görünüm: 3 sütun grid, edit/sil butonları korunur */}
                <div className="sm:hidden">
                  <div className="grid grid-cols-3 gap-2 text-[10px] font-semibold text-gray-500 dark:text-gray-400 px-1">
                    <div>Yıl</div>
                    <div>Müşteri</div>
                    <div>Kârlılık</div>
                  </div>
                  <div className="mt-2">
                    {(goalsData?.goals || []).length > 0 ? (
                      goalsData.goals.map((goal) => (
                        <div key={goal.id} className="grid grid-cols-3 gap-2 items-center px-2 py-3 border-b border-gray-200 dark:border-gray-700">
                          <div className="text-[11px] text-gray-900 dark:text-white font-medium">{goal.year}</div>
                          <div className="text-[11px] text-gray-700 dark:text-gray-300">{showGoals ? goal.customer_target : '******'}</div>
                          <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                            <span>{showGoals ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: goal.profitability_currency || 'TRY' }).format(Number(goal.profitability_target || 0)) : '******'}</span>
                            <span className="ml-2 flex items-center space-x-1">
                              <button
                                onClick={() => handleEditGoal(goal)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="Düzenle"
                              >
                                <FiEdit size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteGoal(goal)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                title="Sil"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Hedef bulunamadı</div>
                    )}
                  </div>
                </div>

                {/* Masaüstü görünüm: mevcut tablo */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Yıl</th>
                        <th>Müşteri Hedefi</th>
                        <th>Kârlılık Hedefi</th>
                        <th>Para Birimi</th>
                        <th>Açıklama</th>
                        <th>Oluşturan</th>
                        <th>Oluşturulma</th>
                        <th>Güncellendi</th>
                        <th className="text-right w-24">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(goalsData?.goals || []).length > 0 ? (
                        goalsData.goals.map(goal => (
                          <tr key={goal.id}>
                            <td>{goal.year}</td>
                            <td>{showGoals ? goal.customer_target : '******'}</td>
                            <td>{showGoals ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: goal.profitability_currency || 'TRY' }).format(Number(goal.profitability_target || 0)) : '******'}</td>
                            <td>{goal.profitability_currency || 'TRY'}</td>
                            <td>{goal.description || '-'}</td>
                            <td>{goal.created_by || '-'}</td>
                            <td>{goal.created_at ? new Date(goal.created_at).toLocaleString('tr-TR') : '-'}</td>
                            <td>{goal.updated_at ? new Date(goal.updated_at).toLocaleString('tr-TR') : '-'}</td>
                            <td className="text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => handleEditGoal(goal)}
                                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                  title="Düzenle"
                                >
                                  <FiEdit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteGoal(goal)}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                  title="Sil"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center py-6 text-gray-500 dark:text-gray-400">Hedef bulunamadı</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Düzenleme modal'ı */}
      {showEditModal && (
        <UserModal
          user={user}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
          }}
        />
      )}

      {/* Şifre sıfırlama modal'ı */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md mx-4 overflow-hidden">
            {/* Modal başlığı */}
            <div className="bg-brand-primary text-white px-6 py-4">
              <h3 className="text-lg font-semibold">
                Şifre Sıfırla
              </h3>
              <p className="text-sm text-gray-200 mt-1">
                {user.user_fullname} kullanıcısının şifresini güncelleyin
              </p>
            </div>
            
            {/* Modal içeriği */}
            <form onSubmit={handlePasswordResetSubmit} className="p-6">
              <div className="space-y-4">
                {/* Yeni şifre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yeni Şifre
                  </label>
                  <input
                    type="password"
                    value={passwordData.password}
                    onChange={(e) => handlePasswordChange('password', e.target.value)}
                    className={`input-field ${passwordErrors.password ? 'input-error' : ''}`}
                    placeholder="Yeni şifre girin"
                  />
                  {passwordErrors.password && (
                    <p className="text-red-500 text-xs mt-1">{passwordErrors.password}</p>
                  )}
                </div>

                {/* Şifre tekrar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Şifre Tekrar
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className={`input-field ${passwordErrors.confirmPassword ? 'input-error' : ''}`}
                    placeholder="Şifreyi tekrar girin"
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Modal butonları */}
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Şifreyi Güncelle
                </button>
                <button
                  type="button"
                  onClick={() => setShowPasswordReset(false)}
                  className="btn-secondary flex-1"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Silme onay modal'ı */}
      {showDeleteModal && (
        <DeleteConfirmModal
          title="Kullanıcıyı Sil"
          message={`"${user.user_fullname}" kullanıcısını silmek istediğinizden emin misiniz?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
          isLoading={false}
        />
      )}

      {/* Hedef ekleme modal'ı */}
      {showGoalModal && (
        <UserGoalModal
          user={user}
          onClose={() => {
            setShowGoalModal(false);
            window.location.reload();
          }}
          onSuccess={() => {
            setShowGoalModal(false);
            refetch();
          }}
        />
      )}

      {/* Hedef düzenleme modal'ı */}
      {showGoalEditModal && editingGoal && (
        <UserGoalModal
          user={user}
          goal={editingGoal}
          onClose={() => {
            setShowGoalEditModal(false);
            setEditingGoal(null);
          }}
          onSuccess={() => {
            setShowGoalEditModal(false);
            setEditingGoal(null);
            refetch();
            window.location.reload();
          }}
        />
      )}

      {/* Hedef silme onay modal'ı */}
      {showGoalDeleteModal && deletingGoal && (
        <DeleteConfirmModal
          isOpen={showGoalDeleteModal}
          title="Hedefi Sil"
          message={`"${deletingGoal.year}" yılına ait hedefi silmek istediğinizden emin misiniz?`}
          onConfirm={confirmDeleteGoal}
          onCancel={() => {
            setShowGoalDeleteModal(false);
            setDeletingGoal(null);
          }}
          isLoading={false}
        />
      )}
    </div>
  );
};

export default UserDetail;
